import React, { useState } from 'react';
import { useMutation } from 'react-query';
import api from '../services/api.ts';
import { formatCurrency } from '../utils/currency.ts';
import { formatStatus, getStatusColor } from '../utils/statusFormatter.ts';
import {
  DocumentChartBarIcon,
  ArrowDownTrayIcon,
  BuildingOfficeIcon,
  TruckIcon,
  BanknotesIcon,
  CurrencyDollarIcon,
  PaperAirplaneIcon,
  HeartIcon,
  // Using TruckIcon for vessels for consistency of style
} from '@heroicons/react/24/outline';
import BoatIcon from '../components/icons/BoatIcon.tsx';

// Formata a categoria de investimento para PT-BR
const formatInvestmentCategory = (category?: string): string => {
  switch (String(category || '').toLowerCase()) {
    case 'stocks':
      return 'Ações';
    case 'fixed_income':
      return 'Renda Fixa';
    case 'funds':
      return 'Fundos';
    case 'crypto':
      return 'Cripto';
    case 'offshore':
      return 'Exterior';
    case 'other':
      return 'Outros';
    default:
      return category || 'N/A';
  }
};

// Formata a categoria de filantropia para PT-BR
const formatPhilanthropyCategory = (category?: string): string => {
  switch (String(category || '').toLowerCase()) {
    case 'education':
      return 'Educação';
    case 'health':
      return 'Saúde';
    case 'poverty':
      return 'Combate à Pobreza';
    case 'culture':
      return 'Cultura';
    case 'environment':
      return 'Meio Ambiente';
    case 'religion':
      return 'Religião';
    case 'other':
      return 'Outros';
    default:
      return category || 'N/A';
  }
};

// Formata a categoria de recebíveis para PT-BR
const formatReceivableCategory = (category?: string): string => {
  switch (String(category || '').toLowerCase()) {
    case 'real_estate':
      return 'Imóvel';
    case 'vehicle':
      return 'Veículo';
    case 'mutuos':
      return 'Mútuos';
    case 'other':
      return 'Outros';
    default:
      return category || 'N/A';
  }
};

interface ReportSummary {
  totalAssets?: number;
  totalValue?: number;
  totalVehicles?: number;
  totalDebts?: number;
  totalAmount?: number;
  totalRemaining?: number;
}

const NewReports: React.FC = () => {
  const [selectedReport, setSelectedReport] = useState<string>('');
  const [reportData, setReportData] = useState<any>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  // Mutation para gerar relatórios
  const generateReportMutation = useMutation(
    async ({ reportType }: { reportType: string }) => {
      let endpoint = '';

      // Map frontend report types to backend endpoints
      switch (reportType) {
        case 'balance-sheet':
          endpoint = '/reports/financial-summary';
          break;
        case 'vehicle-inventory':
          endpoint = '/reports/vehicles';
          break;
        case 'aircrafts':
          endpoint = '/reports/aircrafts';
          break;
        case 'vessels':
          endpoint = '/reports/vessels';
          break;
        case 'debts':
          endpoint = '/reports/debts';
          break;
        case 'receivables':
          endpoint = '/reports/receivables';
          break;
        case 'assets':
          endpoint = '/reports/assets';
          break;
        case 'companies':
          endpoint = '/reports/companies';
          break;
        case 'investments':
          endpoint = '/reports/investments';
          break;
        case 'philanthropies':
          endpoint = '/reports/philanthropies';
          break;
        case 'rentals':
          endpoint = '/reports/rentals';
          break;
        default:
          throw new Error('Tipo de relatório não suportado');
      }

      const response = await api.get(endpoint);
      return response.data;
    },
    {
      onSuccess: data => {
        setReportData(data);
        setIsGenerating(false);
      },
      onError: error => {
        console.error('Erro ao gerar relatório:', error);
        setIsGenerating(false);
      },
    }
  );

  // Mutation para exportar relatórios
  const exportReportMutation = useMutation(
    async ({ reportType, format }: { reportType: string; format: 'excel' | 'pdf' }) => {
      let backendReportType = '';

      // Map frontend report types to backend report types
      switch (reportType) {
        case 'balance-sheet':
          backendReportType = 'financial-summary';
          break;
        case 'vehicle-inventory':
          backendReportType = 'vehicles';
          break;
        case 'aircrafts':
          backendReportType = 'aircrafts';
          break;
        case 'vessels':
          backendReportType = 'vessels';
          break;
        case 'debts':
          backendReportType = 'debts';
          break;
        case 'receivables':
          backendReportType = 'receivables';
          break;
        case 'assets':
          backendReportType = 'assets';
          break;
        case 'companies':
          backendReportType = 'companies';
          break;
        case 'investments':
          backendReportType = 'investments';
          break;
        case 'philanthropies':
          backendReportType = 'philanthropies';
          break;
        case 'rentals':
          backendReportType = 'rentals';
          break;
        default:
          throw new Error('Tipo de relatório não suportado');
      }

      const response = await api.get(`/reports/export/${format}/${backendReportType}`, {
        responseType: 'blob',
      });

      // Criar URL para download
      const blob = new Blob([response.data], {
        type:
          format === 'excel'
            ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            : 'application/pdf',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${getReportTitle(reportType).replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    {
      onError: error => {
        console.error('Erro ao exportar relatório:', error);
        alert('Erro ao exportar relatório. Tente novamente.');
      },
    }
  );

  const handleGenerateReport = async (reportType: string) => {
    setSelectedReport(reportType);
    setIsGenerating(true);
    setReportData(null);
    generateReportMutation.mutate({ reportType });
  };

  const handleExportReport = (format: 'excel' | 'pdf') => {
    if (!selectedReport) return;
    exportReportMutation.mutate({ reportType: selectedReport, format });
  };

  const getReportTitle = (reportType: string): string => {
    switch (reportType) {
      case 'balance-sheet':
        return 'Balanço Patrimonial';
      case 'vehicle-inventory':
        return 'Estoque de Veículos';
      case 'aircrafts':
        return 'Relatório de Aeronaves';
      case 'vessels':
        return 'Relatório de Embarcações';
      case 'debts':
        return 'Relatório de Dívidas';
      case 'receivables':
        return 'Relatório de Recebíveis';
      case 'assets':
        return 'Relatório de Patrimônio';
      case 'companies':
        return 'Relatório de Empresas';
      case 'investments':
        return 'Relatório de Investimentos';
      case 'philanthropies':
        return 'Relatório de Filantropia';
      case 'rentals':
        return 'Relatório de Aluguéis';
      default:
        return 'Relatório';
    }
  };

  const renderReportContent = () => {
    if (!reportData) return null;

    switch (selectedReport) {
      case 'balance-sheet':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-medium text-green-800">Ativos Totais</h4>
                <p className="text-2xl font-bold text-green-600">
                  {formatCurrency(reportData.summary?.totalAssets || 0)}
                </p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg">
                <h4 className="font-medium text-red-800">Dívidas Totais</h4>
                <p className="text-2xl font-bold text-red-600">
                  {formatCurrency(reportData.summary?.totalLiabilities || 0)}
                </p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-medium text-blue-800">Patrimônio Líquido</h4>
                <p className="text-2xl font-bold text-blue-600">
                  {formatCurrency(reportData.summary?.netWorth || 0)}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white p-4 rounded-lg border">
                <h4 className="font-medium text-gray-800 mb-3">Detalhamento dos Ativos</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Ativos Fixos:</span>
                    <span className="font-medium">
                      {formatCurrency(reportData.assets?.fixedAssets || 0)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Veículos:</span>
                    <span className="font-medium">
                      {formatCurrency(reportData.assets?.vehicles || 0)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Embarcações:</span>
                    <span className="font-medium">
                      {formatCurrency(reportData.assets?.vessels || 0)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Aeronaves:</span>
                    <span className="font-medium">
                      {formatCurrency(reportData.assets?.aircrafts || 0)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Investimentos:</span>
                    <span className="font-medium">
                      {formatCurrency(reportData.assets?.investments || 0)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Recebíveis:</span>
                    <span className="font-medium">
                      {formatCurrency(reportData.assets?.receivables || 0)}
                    </span>
                  </div>
                  <div className="flex justify-between border-t pt-2">
                    <span className="font-medium">Total:</span>
                    <span className="font-bold">
                      {formatCurrency(reportData.assets?.total || 0)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-white p-4 rounded-lg border">
                <h4 className="font-medium text-gray-800 mb-3">Detalhamento das Dívidas</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Dívidas Totais:</span>
                    <span className="font-medium">
                      {formatCurrency(reportData.liabilities?.totalDebts || 0)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Dívidas Vencidas:</span>
                    <span className="font-medium text-red-600">
                      {formatCurrency(reportData.liabilities?.overdueDebts || 0)}
                    </span>
                  </div>
                  <div className="flex justify-between border-t pt-2">
                    <span className="font-medium">Índice Dívida/Ativo:</span>
                    <span className="font-bold">
                      {(reportData.summary?.debtToAssetRatio || 0).toFixed(1)}%
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'vehicle-inventory':
        return (
          <div className="space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-medium text-blue-800">Resumo do Estoque</h4>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-2">
                <div>
                  <p className="text-sm text-blue-600">Total de Veículos</p>
                  <p className="text-xl font-bold text-blue-800">
                    {reportData.summary?.totalVehicles || 0}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-blue-600">Valor de Venda</p>
                  <p className="text-xl font-bold text-blue-800">
                    {formatCurrency(reportData.summary?.totalSellingValue || 0)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-blue-600">Valor de Compra</p>
                  <p className="text-xl font-bold text-blue-800">
                    {formatCurrency(reportData.summary?.totalPurchaseValue || 0)}
                  </p>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Veículo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ano
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Placa
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Valor de Compra
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Valor de Venda
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Data de Compra
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {reportData.vehicles?.map((vehicle: any) => (
                    <tr key={vehicle.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {vehicle.make || vehicle.brand} {vehicle.model}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {vehicle.year}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {vehicle.licensePlate}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(vehicle.purchasePrice || 0)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(vehicle.sellingPrice || 0)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {vehicle.purchaseDate
                          ? new Date(vehicle.purchaseDate).toLocaleDateString('pt-BR')
                          : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(vehicle.status)}`}
                        >
                          {formatStatus(vehicle.status)}
                        </span>
                      </td>
                    </tr>
                  )) || []}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 'aircrafts':
        return (
          <div className="space-y-4">
            <div className="bg-sky-50 p-4 rounded-lg">
              <h4 className="font-medium text-sky-800">Resumo de Aeronaves</h4>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-2">
                <div>
                  <p className="text-sm text-sky-600">Total de Aeronaves</p>
                  <p className="text-xl font-bold text-sky-800">
                    {reportData.summary?.totalAircrafts || 0}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-sky-600">Valor de Venda</p>
                  <p className="text-xl font-bold text-sky-800">
                    {formatCurrency(reportData.summary?.totalSellingValue || 0)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-sky-600">Valor de Compra</p>
                  <p className="text-xl font-bold text-sky-800">
                    {formatCurrency(reportData.summary?.totalPurchaseValue || 0)}
                  </p>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Aeronave
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ano
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Matrícula
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Valor de Compra
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Valor de Venda
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Data de Compra
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {(reportData.aircrafts || []).map((aircraft: any) => (
                    <tr key={aircraft.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {(aircraft.make || aircraft.brand) ? `${aircraft.make || aircraft.brand} ${aircraft.model || ''}`.trim() : (aircraft.model || 'N/A')}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {aircraft.year}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {aircraft.tailNumber}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(aircraft.purchasePrice || 0)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(aircraft.sellingPrice || 0)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {aircraft.purchaseDate
                          ? new Date(aircraft.purchaseDate).toLocaleDateString('pt-BR')
                          : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(aircraft.status)}`}
                        >
                          {formatStatus(aircraft.status)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 'vessels':
        return (
          <div className="space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-medium text-blue-800">Resumo de Embarcações</h4>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-2">
                <div>
                  <p className="text-sm text-blue-600">Total de Embarcações</p>
                  <p className="text-xl font-bold text-blue-800">
                    {reportData.summary?.totalVessels || 0}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-blue-600">Valor de Venda</p>
                  <p className="text-xl font-bold text-blue-800">
                    {formatCurrency(reportData.summary?.totalSellingValue || 0)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-blue-600">Valor de Compra</p>
                  <p className="text-xl font-bold text-blue-800">
                    {formatCurrency(reportData.summary?.totalPurchaseValue || 0)}
                  </p>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Embarcação</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ano</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Matrícula</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Valor de Compra</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Valor de Venda</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data de Compra</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {(reportData.vessels || []).map((vessel: any) => (
                    <tr key={vessel.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {(vessel.make || vessel.brand) ? `${vessel.make || vessel.brand} ${vessel.model || ''}`.trim() : (vessel.model || 'N/A')}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{vessel.year}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{vessel.registrationNumber}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(vessel.purchasePrice || 0)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(vessel.sellingPrice || 0)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{vessel.purchaseDate ? new Date(vessel.purchaseDate).toLocaleDateString() : '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(vessel.status)}`}>
                          {formatStatus(vessel.status)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 'debts':
        return (
          <div className="space-y-4">
            <div className="bg-red-50 p-4 rounded-lg">
              <h4 className="font-medium text-red-800">Resumo das Dívidas</h4>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-2">
                <div>
                  <p className="text-sm text-red-600">Total de Dívidas</p>
                  <p className="text-xl font-bold text-red-800">
                    {reportData.summary?.totalDebts || 0}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-red-600">Valor Total</p>
                  <p className="text-xl font-bold text-red-800">
                    {formatCurrency(reportData.summary?.totalAmount || 0)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-red-600">Valor Restante</p>
                  <p className="text-xl font-bold text-red-800">
                    {formatCurrency(reportData.summary?.totalRemaining || 0)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-red-600">Valor Pago</p>
                  <p className="text-xl font-bold text-green-800">
                    {formatCurrency(reportData.summary?.totalPaid || 0)}
                  </p>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Descrição
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Credor
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Valor Total
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Valor Restante
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Vencimento
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {reportData.debts?.map((debt: any) => (
                    <tr key={debt.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{debt.description}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {debt.creditor}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(debt.amount || 0)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(debt.remainingAmount || 0)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {debt.dueDate ? new Date(debt.dueDate).toLocaleDateString('pt-BR') : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(debt.status)}`}
                        >
                          {formatStatus(debt.status)}
                        </span>
                      </td>
                    </tr>
                  )) || []}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 'receivables':
        return (
          <div className="space-y-4">
            <div className="bg-emerald-50 p-4 rounded-lg">
              <h4 className="font-medium text-emerald-800">Resumo dos Recebíveis</h4>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-2">
                <div>
                  <p className="text-sm text-emerald-600">Total de Recebíveis</p>
                  <p className="text-xl font-bold text-emerald-800">
                    {reportData.summary?.totalReceivables || 0}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-emerald-600">Valor Original</p>
                  <p className="text-xl font-bold text-emerald-800">
                    {formatCurrency(reportData.summary?.totalAmount || 0)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-emerald-600">Saldo a Receber</p>
                  <p className="text-xl font-bold text-emerald-800">
                    {formatCurrency(reportData.summary?.totalRemaining || 0)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-emerald-600">Em Atraso</p>
                  <p className="text-xl font-bold text-red-700">
                    {formatCurrency(reportData.summary?.overdueAmount || 0)}
                  </p>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Descrição
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cliente
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Categoria
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Valor Original
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Saldo a Receber
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Início
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Vencimento
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {reportData.receivables?.map((r: any) => (
                    <tr key={r.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{r.description}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {r.customer || r.customerName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatReceivableCategory(r.category || r.type)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(r.originalAmount ?? r.amount ?? 0)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(r.currentAmount ?? r.remainingAmount ?? 0)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {r.startDate ? new Date(r.startDate).toLocaleDateString('pt-BR') : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {r.dueDate ? new Date(r.dueDate).toLocaleDateString('pt-BR') : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(r.status)}`}
                        >
                          {formatStatus(r.status)}
                        </span>
                      </td>
                    </tr>
                  )) || []}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 'assets':
        return (
          <div className="space-y-4">
            <div className="bg-green-50 p-4 rounded-lg">
              <h4 className="font-medium text-green-800">Resumo do Patrimônio</h4>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-2">
                <div>
                  <p className="text-sm text-green-600">Total de Ativos</p>
                  <p className="text-xl font-bold text-green-800">
                    {reportData.summary?.totalAssets || 0}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-green-600">Valor Total</p>
                  <p className="text-xl font-bold text-green-800">
                    {formatCurrency(reportData.summary?.totalValue || 0)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-green-600">Valor de Compra</p>
                  <p className="text-xl font-bold text-green-800">
                    {formatCurrency(reportData.summary?.totalPurchaseValue || 0)}
                  </p>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Nome
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Categoria
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Valor de Compra
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Valor de Venda
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Data de Compra
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {reportData.assets?.map((asset: any) => (
                    <tr key={asset.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {asset.name || asset.description}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {asset.category || asset.type}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(asset.acquisitionValue || 0)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(asset.currentValue || 0)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {asset.acquisitionDate
                          ? new Date(asset.acquisitionDate).toLocaleDateString('pt-BR')
                          : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(asset.status)}`}
                        >
                          {formatStatus(asset.status)}
                        </span>
                      </td>
                    </tr>
                  )) || []}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 'companies':
        return (
          <div className="space-y-4">
            <div className="bg-indigo-50 p-4 rounded-lg">
              <h4 className="font-medium text-indigo-800">Resumo das Empresas</h4>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-2">
                <div>
                  <p className="text-sm text-indigo-600">Total de Empresas</p>
                  <p className="text-xl font-bold text-indigo-800">
                    {reportData.summary?.totalCompanies || 0}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-indigo-600">Receita Anual Total</p>
                  <p className="text-xl font-bold text-indigo-800">
                    {formatCurrency(reportData.summary?.totalAnnualRevenue || 0)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-indigo-600">Lucro Anual Total</p>
                  <p className="text-xl font-bold text-indigo-800">
                    {formatCurrency(reportData.summary?.totalAnnualProfit || 0)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-indigo-600">Valuation Total</p>
                  <p className="text-xl font-bold text-indigo-800">
                    {formatCurrency(reportData.summary?.totalValuation || 0)}
                  </p>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Empresa
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      CNPJ
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Receita Anual
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Lucro Anual
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Valuation
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Margem
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {reportData.companies?.map((company: any) => (
                    <tr key={company.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{company.name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {company.cnpj}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(company.status)}`}
                        >
                          {company.status === 'active' ? 'Ativa' : 'Inativa'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(company.annualRevenue || 0)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(company.annualProfit || 0)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(company.valuation || 0)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{`${Number(company.profitPercent || 0).toFixed(2)}%`}</td>
                    </tr>
                  )) || []}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 'investments':
        return (
          <div className="space-y-4">
            <div className="bg-amber-50 p-4 rounded-lg">
              <h4 className="font-medium text-amber-800">Resumo dos Investimentos</h4>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mt-2">
                <div>
                  <p className="text-sm text-amber-600">Total de Investimentos</p>
                  <p className="text-xl font-bold text-amber-800">
                    {reportData.summary?.totalInvestments || 0}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-amber-600">Valor Investido</p>
                  <p className="text-xl font-bold text-amber-800">
                    {formatCurrency(reportData.summary?.totalInvested || 0)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-amber-600">Valor Atual</p>
                  <p className="text-xl font-bold text-amber-800">
                    {formatCurrency(reportData.summary?.totalCurrent || 0)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-amber-600">Rendimento</p>
                  <p className="text-xl font-bold text-amber-800">
                    {formatCurrency(reportData.summary?.totalYield || 0)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-amber-600">% Rendimento</p>
                  <p className="text-xl font-bold text-amber-800">
                    {(Number(reportData.summary?.yieldPercent || 0)).toFixed(2)}%
                  </p>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Descrição</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Categoria</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Investido</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Atual</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rendimento</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">% Rendimento</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Criado em</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {reportData.investments?.map((inv: any) => (
                    <tr key={inv.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{inv.description}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatInvestmentCategory(inv.category)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(inv.investedValue || 0)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(inv.currentValue || 0)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(inv.yieldAmount || 0)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{Number(inv.yieldPercent || 0).toFixed(2)}%</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(inv.status)}`}>
                          {formatStatus(inv.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {inv.createdAt ? new Date(inv.createdAt).toLocaleDateString('pt-BR') : 'N/A'}
                      </td>
                    </tr>
                  )) || []}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 'philanthropies': {
        const donations = (reportData.philanthropies || reportData.items || []) as any[];
        const totalDonations = reportData.summary?.totalPhilanthropies ?? donations.length;
        const totalDonated = reportData.summary?.totalDonatedAmount ?? donations.reduce((sum, d) => sum + (Number(d.donatedAmount || 0)), 0);
        const totalAvailable = reportData.summary?.totalAvailableAmount ?? donations.reduce((sum, d) => sum + (Number(d.availableAmount ?? (Number(d.amount || 0) - Number(d.donatedAmount || 0)))), 0);
        return (
          <div className="space-y-4">
            <div className="bg-pink-50 p-4 rounded-lg">
              <h4 className="font-medium text-pink-800">Resumo de Filantropia</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
                <div>
                  <p className="text-sm text-pink-600">Total de Doações</p>
                  <p className="text-xl font-bold text-pink-800">
                    {totalDonations}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-pink-600">Valor Total Doado</p>
                  <p className="text-xl font-bold text-pink-800">
                    {formatCurrency(totalDonated)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-pink-600">Valor Total Disponível</p>
                  <p className="text-xl font-bold text-pink-800">
                    {formatCurrency(totalAvailable)}
                  </p>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Descrição</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Categoria</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Destinado</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Doado</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Disponível</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Última Doação</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {(reportData.philanthropies || reportData.items || []).map((donation: any) => (
                    <tr key={donation.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{donation.description}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatPhilanthropyCategory(donation.category)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(Number(donation.amount || 0))}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(Number(donation.donatedAmount || 0))}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(Number(donation.availableAmount ?? (Number(donation.amount || 0) - Number(donation.donatedAmount || 0))))}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {donation.donationDate ? new Date(donation.donationDate).toLocaleDateString('pt-BR') : 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      }

      case 'rentals': {
        const rentals = (reportData.rentals || reportData.items || []) as any[];
        const totalContracts = reportData.summary?.totalRentals ?? rentals.length;
        const totalMonthly = reportData.summary?.totalMonthlyRent ?? rentals.reduce((sum, r) => sum + (Number(r.monthlyRent) || 0), 0);
        return (
          <div className="space-y-4">
            <div className="bg-indigo-50 p-4 rounded-lg">
              <h4 className="font-medium text-indigo-800">Resumo de Aluguéis</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                <div>
                  <p className="text-sm text-indigo-600">Total de Contratos</p>
                  <p className="text-xl font-bold text-indigo-800">{totalContracts}</p>
                </div>
                <div>
                  <p className="text-sm text-indigo-600">Valor Mensal Total</p>
                  <p className="text-xl font-bold text-indigo-800">{formatCurrency(totalMonthly || 0)}</p>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Imóvel</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Inquilino</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Aluguel Mensal</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dia de Pagamento</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Início</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fim</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {rentals.map((r: any) => {
                    const propertyName = r.asset?.description || r.assetDescription || r.assetId;
                    return (
                      <tr key={r.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{r.id}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{propertyName}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{r.tenantName || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(Number(r.monthlyRent || 0))}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{r.paymentDay ?? '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {r.startDate ? new Date(r.startDate).toLocaleDateString('pt-BR') : 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {r.endDate ? new Date(r.endDate).toLocaleDateString('pt-BR') : 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(r.status)}`}>
                            {formatStatus(r.status)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      }

      default:
        return <div className="text-center text-gray-500">Selecione um tipo de relatório</div>;
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl p-8 text-white">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Relatórios</h1>
            <p className="text-blue-100 mt-2">Análises detalhadas do seu patrimônio</p>
          </div>
          <DocumentChartBarIcon className="h-16 w-16 text-blue-200" />
        </div>
      </div>

      {/* Report Types */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
        <div className="mb-6">
          <h2 className="text-lg leading-6 font-medium text-gray-900">Tipos de Relatórios</h2>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Selecione o tipo de relatório que deseja gerar
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          <button
            onClick={() => handleGenerateReport('balance-sheet')}
            className="relative block w-full border-2 border-gray-300 border-dashed rounded-xl p-6 text-center hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
          >
            <CurrencyDollarIcon className="mx-auto h-12 w-12 text-gray-400" />
            <span className="mt-2 block text-sm font-medium text-gray-900">
              Balanço Patrimonial
            </span>
            <span className="mt-1 block text-xs text-gray-500">
              Visão completa: veículos, imóveis, aeronaves, embarcações, investimentos, dívidas e recebíveis
            </span>
          </button>

          <button
            onClick={() => handleGenerateReport('vehicle-inventory')}
            className="relative block w-full border-2 border-gray-300 border-dashed rounded-xl p-6 text-center hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
          >
            <TruckIcon className="mx-auto h-12 w-12 text-gray-400" />
            <span className="mt-2 block text-sm font-medium text-gray-900">
              Estoque de Veículos
            </span>
            <span className="mt-1 block text-xs text-gray-500">Todos os veículos não vendidos</span>
          </button>

          <button
            onClick={() => handleGenerateReport('aircrafts')}
            className="relative block w-full border-2 border-gray-300 border-dashed rounded-xl p-6 text-center hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
          >
            <PaperAirplaneIcon className="mx-auto h-12 w-12 text-gray-400" />
            <span className="mt-2 block text-sm font-medium text-gray-900">
              Relatório de Aeronaves
            </span>
            <span className="mt-1 block text-xs text-gray-500">Todas as aeronaves não vendidas</span>
          </button>

          <button
            onClick={() => handleGenerateReport('vessels')}
            className="relative block w-full border-2 border-gray-300 border-dashed rounded-xl p-6 text-center hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
          >
            <BoatIcon className="mx-auto h-12 w-12 text-gray-400" />
            <span className="mt-2 block text-sm font-medium text-gray-900">
              Relatório de Embarcações
            </span>
            <span className="mt-1 block text-xs text-gray-500">Todas as embarcações não vendidas</span>
          </button>

          <button
            onClick={() => handleGenerateReport('debts')}
            className="relative block w-full border-2 border-gray-300 border-dashed rounded-xl p-6 text-center hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
          >
            <BanknotesIcon className="mx-auto h-12 w-12 text-gray-400" />
            <span className="mt-2 block text-sm font-medium text-gray-900">
              Relatório de Dívidas
            </span>
            <span className="mt-1 block text-xs text-gray-500">Todas as dívidas não pagas</span>
          </button>

          <button
            onClick={() => handleGenerateReport('receivables')}
            className="relative block w-full border-2 border-gray-300 border-dashed rounded-xl p-6 text-center hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
          >
            <BanknotesIcon className="mx-auto h-12 w-12 text-gray-400" />
            <span className="mt-2 block text-sm font-medium text-gray-900">
              Relatório de Recebíveis
            </span>
            <span className="mt-1 block text-xs text-gray-500">Todos os recebíveis em aberto</span>
          </button>

          <button
            onClick={() => handleGenerateReport('assets')}
            className="relative block w-full border-2 border-gray-300 border-dashed rounded-xl p-6 text-center hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
          >
            <BuildingOfficeIcon className="mx-auto h-12 w-12 text-gray-400" />
            <span className="mt-2 block text-sm font-medium text-gray-900">
              Relatório de Patrimônio
            </span>
            <span className="mt-1 block text-xs text-gray-500">Todo o patrimônio ativo</span>
          </button>

          <button
            onClick={() => handleGenerateReport('rentals')}
            className="relative block w-full border-2 border-gray-300 border-dashed rounded-xl p-6 text-center hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
          >
            <BuildingOfficeIcon className="mx-auto h-12 w-12 text-gray-400" />
            <span className="mt-2 block text-sm font-medium text-gray-900">
              Relatório de Aluguéis
            </span>
            <span className="mt-1 block text-xs text-gray-500">Contratos e valores mensais</span>
          </button>

          <button
            onClick={() => handleGenerateReport('companies')}
            className="relative block w-full border-2 border-gray-300 border-dashed rounded-xl p-6 text-center hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
          >
            <BuildingOfficeIcon className="mx-auto h-12 w-12 text-gray-400" />
            <span className="mt-2 block text-sm font-medium text-gray-900">
              Relatório de Empresas
            </span>
            <span className="mt-1 block text-xs text-gray-500">
              Dados cadastrais e financeiros das empresas
            </span>
          </button>

          <button
            onClick={() => handleGenerateReport('investments')}
            className="relative block w-full border-2 border-gray-300 border-dashed rounded-xl p-6 text-center hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
          >
            <BanknotesIcon className="mx-auto h-12 w-12 text-gray-400" />
            <span className="mt-2 block text-sm font-medium text-gray-900">
              Relatório de Investimentos
            </span>
            <span className="mt-1 block text-xs text-gray-500">Resumo e lista de investimentos</span>
          </button>

          <button
            onClick={() => handleGenerateReport('philanthropies')}
            className="relative block w-full border-2 border-gray-300 border-dashed rounded-xl p-6 text-center hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
          >
            <HeartIcon className="mx-auto h-12 w-12 text-gray-400" />
            <span className="mt-2 block text-sm font-medium text-gray-900">
              Relatório de Filantropia
            </span>
            <span className="mt-1 block text-xs text-gray-500">Doações por categoria e período</span>
          </button>
        </div>
      </div>

      {/* Report Results */}
      {reportData && (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-medium text-gray-900">{getReportTitle(selectedReport)}</h3>

            <div className="flex space-x-2">
              <button
                onClick={() => handleExportReport('excel')}
                disabled={exportReportMutation.isLoading}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
              >
                <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                Excel
              </button>

              <button
                onClick={() => handleExportReport('pdf')}
                disabled={exportReportMutation.isLoading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
              >
                <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                PDF
              </button>
            </div>
          </div>

          {renderReportContent()}
        </div>
      )}

      {/* Loading State */}
      {isGenerating && (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-12">
          <div className="flex flex-col items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
            <p className="text-gray-600">Gerando relatório...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewReports;
